import { CreateBox } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import '@babylonjs/core/Rendering/edgesRenderer'; // augments Mesh.enableEdgesRendering
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import type { Scene } from '@babylonjs/core/scene';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { PALETTE } from '@observant/shared';
import type { LevelDef } from '../level/types.js';
import { TIMELINE } from '../level/timeline.js';
import { neonMaterial, darkMaterial } from './neonMaterial.js';

function edge(mesh: Mesh, hex: string, width = 3): void {
  const c = Color3.FromHexString(hex);
  mesh.enableEdgesRendering();
  mesh.edgesWidth = width;
  mesh.edgesColor = new Color4(c.r, c.g, c.b, 1);
}

export interface LevelMeshes {
  gate: Mesh;
  beam: Mesh;
  handleHigh: Mesh;
  handleCorner: Mesh;
  link: Mesh;
}

/** Build the runner track + entities (laid along +Z). Returns the dynamic pieces. */
export function buildLevelMeshes(scene: Scene, level: LevelDef): LevelMeshes {
  const dark = darkMaterial(scene);
  const w = level.halfWidth * 2;
  const mid = level.trackLength / 2;

  // Floor strip.
  const floor = CreateBox('floor', { width: w, height: 1, depth: level.trackLength }, scene);
  floor.position.set(0, level.floorY - 0.5, mid);
  floor.material = dark;
  edge(floor, PALETTE.neonCyan, 2);

  // Side rails.
  for (const sx of [-level.halfWidth, level.halfWidth]) {
    const rail = CreateBox('rail', { width: 0.15, height: 0.4, depth: level.trackLength }, scene);
    rail.position.set(sx, level.floorY + 0.2, mid);
    rail.material = neonMaterial(scene, PALETTE.neonCyan, 0.9, `rail-${sx}`);
  }

  // Obstacles.
  for (let i = 0; i < level.obstacles.length; i++) {
    const o = level.obstacles[i]!;
    const ow = o.xMax - o.xMin;
    const od = o.zMax - o.zMin;
    const cx = (o.xMin + o.xMax) / 2;
    const cz = (o.zMin + o.zMax) / 2;
    let height: number;
    let cy: number;
    let color: string;
    if (o.kind === 'hurdle') {
      height = o.topY ?? 1;
      cy = level.floorY + height / 2;
      color = PALETTE.neonAmber;
    } else if (o.kind === 'bar') {
      height = 0.4;
      cy = (o.botY ?? 1) + height / 2;
      color = PALETTE.neonMagenta;
    } else {
      height = 2.6;
      cy = level.floorY + height / 2;
      color = PALETTE.neonCyan;
    }
    const m = CreateBox(`obstacle-${i}`, { width: ow, height, depth: od }, scene);
    m.position.set(cx, cy, cz);
    m.material = neonMaterial(scene, color, 0.4, `obstacle-mat-${i}`);
    edge(m, color, 2);
  }

  // History boards: standing 3D panels at the road edges, year/title drawn on
  // them, angled to face the approaching player.
  for (let i = 0; i < level.boards.length; i++) {
    const b = level.boards[i]!;
    const ev = TIMELINE[b.timelineIndex]!;

    // Support post.
    const post = CreateBox(`board-post-${i}`, { width: 0.14, height: 1.7, depth: 0.14 }, scene);
    post.position.set(b.x, level.floorY + 0.85, b.z);
    post.material = neonMaterial(scene, PALETTE.neonCyan, 0.7, `board-post-mat-${i}`);

    // Lit panel with the text drawn on it.
    const tex = new DynamicTexture(`board-tex-${i}`, { width: 512, height: 256 }, scene, false);
    const ctx = tex.getContext() as unknown as CanvasRenderingContext2D;
    ctx.fillStyle = '#061226';
    ctx.fillRect(0, 0, 512, 256);
    ctx.textAlign = 'center';
    ctx.fillStyle = '#16f4ff';
    ctx.font = 'bold 112px monospace';
    ctx.fillText(ev.year, 256, 120);
    ctx.fillStyle = '#ff2bd6';
    ctx.font = 'bold 52px monospace';
    ctx.fillText(ev.title, 256, 196);
    tex.update();
    // The panel faces back down the track, so we read its reverse side — flip U.
    tex.uScale = -1;
    tex.uOffset = 1;

    const mat = new StandardMaterial(`board-mat-${i}`, scene);
    mat.emissiveTexture = tex; // emissiveColor stays black so the texture isn't washed out
    mat.diffuseColor = new Color3(0, 0, 0);
    mat.disableLighting = true;
    mat.backFaceCulling = false;

    const panel = CreatePlane(`board-${i}`, { width: 2.6, height: 1.6 }, scene);
    panel.position.set(b.x, level.floorY + 2.2, b.z);
    panel.material = mat;
    edge(panel, PALETTE.neonCyan, 2);
    // Face back down the track (toward the approaching player) and inward.
    panel.lookAt(new Vector3(0, level.floorY + 2.2, b.z - 14));
  }

  // Gate wall (blocks forward until complete).
  const gate = CreateBox('gate', { width: w, height: 4, depth: 0.4 }, scene);
  gate.position.set(0, level.floorY + 2, level.gateZ);
  gate.material = neonMaterial(scene, PALETTE.neonAmber, 0.3, 'gate-mat');
  edge(gate, PALETTE.neonAmber, 3);

  // Scanner beam — hidden until activated.
  const beam = CreateBox(
    'beam',
    { width: level.beam.w, height: level.beam.h, depth: level.beam.d },
    scene,
  );
  beam.position.set(level.beam.x, level.floorY + level.beam.h / 2, level.beam.z);
  beam.material = neonMaterial(scene, PALETTE.neonAmber, 1.4, 'beam-mat');
  beam.visibility = 0.4;
  beam.setEnabled(false);

  // Handles.
  const handleHigh = CreateBox('handle-high', { width: 0.5, height: 0.5, depth: 0.5 }, scene);
  handleHigh.position.set(level.handleHigh.x, level.handleHigh.y, level.handleHigh.z);
  handleHigh.material = neonMaterial(scene, PALETTE.neonAmber, 1, 'handle-high-mat');
  edge(handleHigh, PALETTE.neonAmber, 3);

  const handleCorner = CreateBox('handle-corner', { width: 0.5, height: 0.5, depth: 0.5 }, scene);
  handleCorner.position.set(level.handleCorner.x, level.handleCorner.y, level.handleCorner.z);
  handleCorner.material = neonMaterial(scene, PALETTE.neonCyan, 1, 'handle-corner-mat');
  edge(handleCorner, PALETTE.neonCyan, 3);

  // Entanglement link between the handles.
  const dx = level.handleHigh.x - level.handleCorner.x;
  const dy = level.handleHigh.y - level.handleCorner.y;
  const dz = level.handleHigh.z - level.handleCorner.z;
  const len = Math.hypot(dx, dy, dz);
  const link = CreateBox('entangle-link', { width: 0.06, height: 0.06, depth: len }, scene);
  link.position.set(
    (level.handleHigh.x + level.handleCorner.x) / 2,
    (level.handleHigh.y + level.handleCorner.y) / 2,
    (level.handleHigh.z + level.handleCorner.z) / 2,
  );
  link.lookAt(handleHigh.position);
  link.material = neonMaterial(scene, PALETTE.neonMagenta, 1.5, 'link-mat');
  link.setEnabled(false);

  return { gate, beam, handleHigh, handleCorner, link };
}
